import { IconUsers } from "@tabler/icons-react";
import {
  differenceInCalendarDays,
  format,
  isValid,
  parseISO,
  subDays,
} from "date-fns";
import { useIntersectionObserver } from "usehooks-ts";
import { ANALYTICS_QUERY } from "../../constants";
import { useAnalyticsQuery } from "../../hooks";
import { useAnalyticsStoreShallow } from "../../store/analytics.store";
import { MetricItem } from "./metric-item";

type MetricData = {
  label: string;
  value: string;
  change: number;
  tooltip?: string;
  error: string;
};

type MetricKey =
  | "uniqueReaders"
  | "totalSessions"
  | "engagedUsers"
  | "avgReadingTime";

const METRIC_ORDER: MetricKey[] = [
  "uniqueReaders",
  "totalSessions",
  "engagedUsers",
  "avgReadingTime",
];

type StatsData = {
  sessions: number;
  visitors: number;
  avg_session_duration: string;
  clicked_cta_percentage: number;
};

// Calculate percentage change between current and previous values
const calculateChange = (current: number, previous: number): number => {
  if (!previous || previous === 0) return 0;
  return Math.round(((current - previous) / previous) * 100);
};

// Format number with commas
const formatNumber = (num: number): string => {
  return num.toLocaleString("en-US");
};

// Convert time string "MM:SS" to seconds
const timeToSeconds = (timeStr: string): number => {
  if (!timeStr) return 0;
  const parts = timeStr.split(":");
  if (parts.length !== 2) return 0;
  const minutes = parseInt(parts[0], 10) || 0;
  const seconds = parseInt(parts[1], 10) || 0;
  return minutes * 60 + seconds;
};

export const AudienceOverview = ({
  overrides,
}: {
  overrides?: Record<string, unknown>;
}) => {
  const dateRange = useAnalyticsStoreShallow((s) => ({
    dateFrom: s.date.dateFrom,
    dateTo: s.date.dateTo,
  }));

  // Calculate previous period dates for trend comparison
  const previousPeriod = (() => {
    if (!dateRange.dateFrom || !dateRange.dateTo) {
      return { dateFrom: dateRange.dateFrom, dateTo: dateRange.dateTo };
    }

    const currentFrom = parseISO(dateRange.dateFrom);
    const currentTo = parseISO(dateRange.dateTo);

    if (!isValid(currentFrom) || !isValid(currentTo)) {
      return { dateFrom: dateRange.dateFrom, dateTo: dateRange.dateTo };
    }

    // Calculate the number of days in the current period (inclusive)
    const periodDays = differenceInCalendarDays(currentTo, currentFrom) + 1;

    // Calculate previous period: go back by the same number of days
    const previousTo = subDays(currentFrom, 1);
    const previousFrom = subDays(previousTo, periodDays - 1);

    return {
      dateFrom: format(previousFrom, "yyyy-MM-dd"),
      dateTo: format(previousTo, "yyyy-MM-dd"),
    };
  })();
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.1,
  });

  // Current period data
  const {
    data,
    isLoading,
    isError: isErrorStats,
  } = useAnalyticsQuery({
    query: [ANALYTICS_QUERY.STATS],
    enabled: isIntersecting,
    overrides: overrides ?? {},
  });

  // Previous period data for trend comparison
  const {
    data: trendData,
    isLoading: isLoadingTrend,
    isError: isErrorTrend,
  } = useAnalyticsQuery({
    query: [ANALYTICS_QUERY.STATS],
    enabled: isIntersecting,
    overrides: {
      dateFrom: previousPeriod.dateFrom,
      dateTo: previousPeriod.dateTo,
      ...(overrides ?? {}),
    },
  });

  const loading = isLoading || isLoadingTrend;
  const error = isErrorStats || isErrorTrend;
  const stats = data?.[ANALYTICS_QUERY.STATS] as StatsData | undefined;
  const trendStats = trendData?.[ANALYTICS_QUERY.STATS] as
    | StatsData
    | undefined;

  // Calculate metrics with trend comparison
  const currentVisitors = stats?.visitors || 0;
  const previousVisitors = trendStats?.visitors || 0;
  const currentSessions = stats?.sessions || 0;
  const previousSessions = trendStats?.sessions || 0;
  const currentCtaPercentage = stats?.clicked_cta_percentage || 0;
  const previousCtaPercentage = trendStats?.clicked_cta_percentage || 0;

  const metrics: Record<MetricKey, MetricData> = {
    uniqueReaders: {
      label: "Unique readers",
      value: formatNumber(currentVisitors),
      change: calculateChange(currentVisitors, previousVisitors),
      error: "--",
    },
    totalSessions: {
      label: "Total sessions",
      value: formatNumber(currentSessions),
      change: calculateChange(currentSessions, previousSessions),
      error: "--",
    },
    engagedUsers: {
      label: "Engaged users",
      value: `${currentCtaPercentage}%`,
      change: calculateChange(currentCtaPercentage, previousCtaPercentage),
      error: "--%",
      tooltip:
        "Shows how many readers engaged with your document through clicks or interactions",
    },
    avgReadingTime: {
      label: "Avg. reading time",
      value: stats?.avg_session_duration || "00:00",
      error: "--:--",
      change: calculateChange(
        timeToSeconds(stats?.avg_session_duration || ""),
        timeToSeconds(trendStats?.avg_session_duration || ""),
      ),
    },
  };

  return (
    <div
      ref={ref}
      className="tw-flex tw-flex-col tw-gap-4 tw-p-5 tw-bg-white tw-outline tw-outline-1 tw-outline-blue/5 tw-rounded-xl tw-shadow-2xs"
    >
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <IconUsers className="tw-size-4" />
        <h2 className="tw-font-sans tw-text-base tw-font-bold tw-text-blue">
          Audience overview
        </h2>
      </div>

      {/* Metrics Grid */}
      <div className="tw-grid tw-grid-cols-2 tw-gap-4">
        {METRIC_ORDER.map((key) => (
          <MetricItem
            key={key}
            metric={metrics[key]}
            loading={loading}
            error={error}
          />
        ))}
      </div>
    </div>
  );
};
